<script setup lang="ts">
import { type Breakpoints, useBreakpoints } from '@vueuse/core'
import { type ComputedRef, computed } from 'vue'
import { shallowRef, watchEffect } from 'vue'
import type {
    IProgressStepsProps,
    TBreakpointsKeys,
    TProgressStepsElementsConfig,
    TProgressStepsOrientation,
} from './types'
import {
    PStepper,
    PStepperDescription,
    PStepperIndicator,
    PStepperItem,
    PStepperSeparator,
    PStepperTitle,
    PStepperTrigger,
} from './parts'
import { useProgressStepsConfig } from './composables/useProgressStepsConfig'
import { EBreakpoints } from '@/ui/composables/useBreakpoint'
import { isDark } from '@/lib/utils'

const props = withDefaults(defineProps<IProgressStepsProps>(), {
    orientation: 'horizontal',
    size: 'MEDIUM',
    isClickable: false,
})

const model = defineModel({ type: Number, default: 0 })

const breakpoints = {} as Breakpoints<TBreakpointsKeys>
Object.entries(EBreakpoints).forEach(([key, value]) => {
    breakpoints[key as unknown as TBreakpointsKeys] = +value
})

const currentOrientation = computed<TProgressStepsOrientation>(() => {
    if (typeof props.orientation === 'string') {
        return props.orientation
    }

    const reactiveBreakpoints = useBreakpoints(breakpoints)

    const screen = reactiveBreakpoints.active() as ComputedRef<TBreakpointsKeys | undefined>

    if (screen.value) {
        return props.orientation[screen.value]
    }

    return 'horizontal'
})

const progressStepsConfig = shallowRef<TProgressStepsElementsConfig>(
    useProgressStepsConfig(props, currentOrientation.value),
)

watchEffect(() => {
    progressStepsConfig.value = useProgressStepsConfig(props, currentOrientation.value)
})
</script>

<template>
    <PStepper
        v-model="model"
        :class="[progressStepsConfig.root, isDark(dark)]"
        data-gpp-tools-ui
    >
        <PStepperItem
            v-for="(item, index) in steps"
            :key="item.step"
            v-slot="{ state }"
            :style="`flex-basis: ${100 / steps.length}%;`"
            :step="item.step"
            :data-last="index === steps.length - 1"
        >
            <div :class="progressStepsConfig.item">
                <div :class="progressStepsConfig.stepBlock">
                    <PStepperTrigger :is-clickable="isClickable">
                        <PStepperIndicator :size="size">
                            <slot
                                name="indicator_content"
                                :step="item.step"
                                :size
                                :state
                            >
                                <VIcon
                                    v-if="state === 'completed'"
                                    name="check"
                                    :size
                                />
                                <span
                                    v-else
                                    class="font-default text-center font-semibold"
                                    :class="{
                                        'label-xs': size === 'SMALL',
                                        'label-lg': size === 'MEDIUM',
                                    }"
                                >
                                    {{ item.step }}
                                </span>
                            </slot>
                        </PStepperIndicator>
                    </PStepperTrigger>

                    <PStepperSeparator
                        v-if="item.step !== steps[steps.length - 1].step"
                        :orientation="currentOrientation"
                        :size="size"
                    />
                </div>
                <div :class="progressStepsConfig.textBlock">
                    <slot
                        name="title"
                        :title="item?.title"
                        classes="text-neutral-primary font-default line-clamp-3 font-semibold"
                    >
                        <PStepperTitle
                            v-if="item?.title"
                            :title="item.title"
                            :class="progressStepsConfig.title"
                        />
                    </slot>
                    <slot
                        name="description"
                        :description="item?.description"
                        classes="font-default text-neutral-primary font-medium"
                    >
                        <PStepperDescription
                            v-if="item?.description"
                            :description="item.description"
                            :class="progressStepsConfig.description"
                        />
                    </slot>

                    <slot
                        name="placeholder"
                        :step
                    />

                    <slot
                        :name="`placeholder_${item.step}`"
                        :step
                    />
                </div>
            </div>
        </PStepperItem>
    </PStepper>
</template>
